import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import css from '@styled-system/css';
const TabList = styled.div.attrs({
    role: 'tablist',
})(({ theme: { colors } }) => ({
    borderRadius: '24px',
    boxShadow: `0 24px 80px ${colors.surfaceShadow}`,
    display: 'inline-flex',
    flexDirection: 'column',
    flexGrow: 0,
    padding: '0 16px',
    position: 'relative',
}));
const Tab = styled.button.attrs({
    role: 'tab',
})((props) => css({
    width: 48,
    height: 48,
    backgroundColor: 'bg.transparent',
    border: 0,
    borderRadius: 'sm',
    color: 'text.black',
    cursor: 'pointer',
    fontWeight: props['aria-selected'] === 'true' ? 'bold' : 'normal',
    margin: '16px 0',
    outline: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        transition: 'all 0.2s ease-in-out',
        backgroundColor: props['aria-selected'] !== 'true' ? 'bg.blueGray' : 'bg.transparent',
    },
}));
const ActiveTabIndicator = styled.div(css({
    width: 48,
    height: 48,
    backgroundColor: 'bg.regular',
    border: 'thinSolid',
    borderColor: 'border.regular',
    borderRadius: 'sm',
    position: 'absolute',
    top: 0,
    left: 16,
    transition: 'all 0.25s ease-in-out',
}));
/**
 * Tabs component tries to follow WAI-ARIA Authoring Practices for
 * [Tabs with Automatic Activation](https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-1/tabs.html)
 */
const VerticalTabs = ({ 'aria-label': ariaLabel, tabLabels, onChange, selectedKey, containerStyles, 'data-testid': dataTestId, }) => {
    const tabs = Object.entries(tabLabels);
    const noOfTabs = tabs.length;
    const tabKeys = Object.keys(tabLabels);
    const [selectedTab, setSelectedTab] = useState(selectedKey);
    const changeTab = (key) => {
        setSelectedTab(key);
        if (onChange)
            onChange(key);
    };
    const tabsRef = useRef({});
    setTimeout(() => {
        var _a;
        (_a = tabsRef.current[selectedTab]) === null || _a === void 0 ? void 0 : _a.focus();
    }, 0);
    // render.resize
    const indicatorRef = useRef(null);
    useEffect(() => {
        var _a;
        if (indicatorRef.current !== null) {
            indicatorRef.current.style.top = `${(_a = tabsRef.current[selectedTab]) === null || _a === void 0 ? void 0 : _a.offsetTop}px`;
        }
    });
    useEffect(() => {
        setSelectedTab(selectedKey);
    }, [selectedKey]);
    const handleClick = (key) => () => {
        changeTab(key);
    };
    const handleKeyDown = (event) => {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                changeTab(selectedTab);
                break;
            case 'ArrowUp':
                event.preventDefault();
                changeTab(selectedTab);
                break;
            case 'Home':
                changeTab(tabKeys[0]);
                break;
            case 'End':
                changeTab(tabKeys[noOfTabs - 1]);
                break;
            default:
                break;
        }
    };
    return (React.createElement(TabList, { "aria-label": ariaLabel, style: containerStyles, "data-testid": dataTestId },
        React.createElement(ActiveTabIndicator, { ref: indicatorRef }),
        tabs.map(([id, Label]) => (React.createElement(Tab, { key: id, onClick: handleClick(id), onKeyDown: handleKeyDown, ref: (ref) => {
                tabsRef.current[id] = ref;
            }, "aria-selected": selectedTab === id ? 'true' : 'false', "aria-controls": id, tabIndex: selectedTab !== id ? -1 : undefined, "data-testid": `${dataTestId}-${id}` },
            React.createElement(Label, { isActive: selectedTab === id }))))));
};
export default VerticalTabs;
